import { useMutation } from '@tanstack/vue-query'
import { memberPinVerifyApi } from '@/api'
import type { memberPinVerifyRequest, memberPinVerifyResponse } from '@/api/member/types'
import type { ApiResponse } from '@/api/types'

export type PinVerifyError = ApiResponse<memberPinVerifyResponse>

export function getPinVerifyErrorMessage(error: PinVerifyError): string {
  const data = error.data
  if (!data) {
    return error.message || '操作失败，请重试'
  }
  if (data.locked) {
    return `密码已锁定，请等待 ${data.left_secs} 秒后重试`
  }
  if (data.left_count !== undefined) {
    if (data.max_count !== undefined) {
      return `密码错误，还可以尝试 ${data.left_count} 次，最多 ${data.max_count} 次`
    }
    return `密码错误，还可以尝试 ${data.left_count} 次`
  }
  if (data.fail_count > 0) {
    return `密码错误，还可以尝试 ${data.left_count} 次`
  }
  return error.message || '操作失败，请重试'
}

export function isPinVerifyError(error: unknown): error is PinVerifyError {
  return typeof error === 'object' && error !== null && 'data' in error
}

/** 从 API 拦截器 reject 的对象中提取 message */
export function getErrorMessage(error: unknown, fallback: string): string {
  return (error as { message?: string }).message || fallback
}

type UsePinVerifyMutationOptions = {
  onSuccess?: (hash: string) => void
  onError?: (message: string, error: PinVerifyError) => void
}

export function usePinVerifyMutation(options?: UsePinVerifyMutationOptions) {
  const mutation = useMutation({
    mutationFn: (data: memberPinVerifyRequest) => memberPinVerifyApi(data),
    onSuccess: (response) => {
      options?.onSuccess?.(response.data.hash)
    },
    onError: (error: PinVerifyError) => {
      options?.onError?.(getPinVerifyErrorMessage(error), error)
    },
  })

  return {
    verifyPin: mutation.mutate,
    verifyPinAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  }
}
